import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectAccountOnboarding } from '@stripe/react-connect-js';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { stripeConnectTheme } from '../config';
import './OnboardingScreen.css';

const OnboardingScreen: React.FC = () => {
  const { currentUser, refreshCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeConnect, setStripeConnect] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeOnboarding = async () => {
      try {
        setIsLoading(true);
        
        // Check if user has a Stripe Connected Account
        if (!currentUser?.stripeDetails?.stripeConnectedAccountId) {
          setError('Your account is not properly set up. Please contact support.');
          setIsLoading(false);
          return;
        }
        
        // Create onboarding session
        const returnUrl = `${window.location.origin}/profile`;
        const response = await apiClient.stripe.createAccountOnboardingSession(returnUrl);
        setClientSecret(response.clientSecret);
        
        // Update onboarding status to InProgress
        await apiClient.stripe.updateOnboardingStatus('InProgress');
        
        // Initialize Stripe Connect
        const stripeConnectInstance = await loadConnectAndInitialize({
          publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
          fetchClientSecret: async () => response.clientSecret,
          appearance: stripeConnectTheme,
        });
        
        setStripeConnect(stripeConnectInstance);
        setIsLoading(false);
      } catch (error: any) {
        console.error('Error initializing onboarding:', error);
        
        // Handle Stripe-specific errors
        if (error.response?.data?.errorType) {
          const errorData = error.response.data;
          setError(`${errorData.message}${errorData.resolution ? ` ${errorData.resolution}` : ''}`);
        } else {
          setError('Failed to initialize onboarding. Please try again later.');
        }
        
        setIsLoading(false);
      }
    };

    initializeOnboarding();
  }, [currentUser, refreshCurrentUser]);

  const handleOnboardingComplete = async () => {
    try {
      // Update onboarding status to Complete
      await apiClient.stripe.updateOnboardingStatus('Complete');
      
      // Refresh user data
      await refreshCurrentUser();
      
      // Navigate back to profile
      navigate('/profile');
    } catch (error) {
      console.error('Error handling onboarding completion:', error);
    }
  };

  const handleOnboardingError = async (error: any) => {
    console.error('Onboarding error:', error);
    
    try {
      // Update onboarding status to Incomplete
      await apiClient.stripe.updateOnboardingStatus('Incomplete');
      
      // Set error message
      setError('There was an error during the onboarding process. Please try again later.');
    } catch (err) {
      console.error('Error handling onboarding error:', err);
    }
  };

  const handleCancel = async () => {
    try {
      // Update onboarding status to Incomplete
      await apiClient.stripe.updateOnboardingStatus('Incomplete');
      
      // Navigate back to profile
      navigate('/profile');
    } catch (error) {
      console.error('Error handling cancel:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="onboarding-screen">
        <div className="onboarding-loading">
          <h2>Loading onboarding...</h2>
          <p>Please wait while we set up your account.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="onboarding-screen">
        <div className="onboarding-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/profile')}>Return to Profile</button>
        </div>
      </div>
    );
  }

  if (!stripeConnect || !clientSecret) {
    return (
      <div className="onboarding-screen">
        <div className="onboarding-error">
          <h2>Unable to load onboarding</h2>
          <p>There was an issue loading the onboarding process. Please try again later.</p>
          <button onClick={() => navigate('/profile')}>Return to Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-screen">
      <div className="onboarding-header">
        <h2>Complete Your Account Setup</h2>
        <p>Please provide the required information to set up your account for payments.</p>
      </div>
      
      <div className="onboarding-container">
        <ConnectComponentsProvider connectInstance={stripeConnect}>
          <ConnectAccountOnboarding
            onExit={async () => {
              console.log('Onboarding exited');
              
              // Check verification status
              try {
                const verificationStatus = await apiClient.stripe.getVerificationStatus();
                
                if (verificationStatus.status === 'Required') {
                  // Show verification required notification
                  setError('Identity verification is required. Please complete the verification process.');
                } else {
                  // Handle normal exit
                  handleCancel();
                }
              } catch (error) {
                console.error('Error checking verification status:', error);
                handleCancel();
              }
            }}
            recipientTermsOfServiceUrl='https://www.veripay.com.au/terms'
            privacyPolicyUrl='https://www.veripay.com.au/privacy'
            onStepChange={(stepChange) => {
              console.log('Onboarding step change:', stepChange);
              // Check if the user has completed all required steps
              // This is a simplified approach since we don't know the exact StepChange type
              if (stepChange && typeof stepChange === 'object') {
                // When the user completes all steps, check the account status
                apiClient.stripe.getOnboardingStatus().then(response => {
                  if (response.status === 'Complete') {
                    handleOnboardingComplete();
                  }
                }).catch(error => {
                  console.error('Error checking onboarding status:', error);
                });
              }
            }}
          />
        </ConnectComponentsProvider>
      </div>
      
      <div className="onboarding-footer">
        <button className="cancel-button" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default OnboardingScreen;